import { useRef, useState } from "react";
import { useDbClient } from "@tanstack/react-db";
import type { WikiPagePublic } from "@softmax/observatory-client/types";
import type { WikiIdentity } from "../wiki.collections";
import type { SetMode, SetError } from "./wiki-editor.types";
import { revertWikiRevision } from "../wiki.mutations";

export function useWikiRevert({
  wikiSlug,
  pageSlug,
  elevatedPrivileges,
  page,
  setMode,
  setError,
}: WikiIdentity & {
  readonly page: WikiPagePublic | null;
  readonly setMode: SetMode;
  readonly setError: SetError;
}) {
  const db = useDbClient();
  const [revertingId, setRevertingId] = useState<string | null>(null);
  const revertActions = useRef(new Map<string, string>());
  function revert(revisionId: string) {
    if (!page) return;

    const key = revertActions.current.get(revisionId) ?? crypto.randomUUID();
    revertActions.current.set(revisionId, key);
    setRevertingId(revisionId);
    setError(null);
    revertWikiRevision({
      db,
      wikiSlug,
      pageSlug,
      elevatedPrivileges,
      pageId: page.id,
      revisionId,
      request: {
        base_revision_id: page.current_revision_id,
        idempotency_key: key,
        note: `Revert to ${revisionId.split("-")[0]}`,
      },
    })
      .then(() => {
        revertActions.current.delete(revisionId);
        setMode("read");
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Revert failed"),
      )
      .finally(() => setRevertingId(null));
  }

  return { revertingId, revert };
}
